import React, { useEffect, useState } from "react";
import SEOHead from "@/components/SEOHead";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import ActiveTrialBanner from "@/components/ActiveTrialBanner";
import MetodoCruzDia from "@/components/metodos/MetodoCruzDia";
import EsquemaInfalivel from "@/components/metodos/EsquemaInfalivel";
import DezenasPremiadas from "@/components/metodos/DezenasPremiadas";
import Metodo777 from "@/components/metodos/Metodo777";
import MetodoSequencia from "@/components/metodos/MetodoSequencia";
import CentenaBichos from "@/components/metodos/CentenaBichos";
import SeteGruposDezenas from "@/components/metodos/SeteGruposDezenas";
import CentenasViciadas from "@/components/metodos/CentenasViciadas";
import BioNumericoStellar from "@/components/metodos/BioNumericoStellar";
import { NumerologiaNome } from "@/components/metodos/NumerologiaNome";
import SomaBahiaCalculator from "@/components/metodos/SomaBahiaCalculator";
import CalculadoraChaveOuro from "@/components/metodos/CalculadoraChaveOuro";
import AcharCentenaDia from "@/components/metodos/AcharCentenaDia";
import JogoBichoAI from "@/components/metodos/JogoBichoAI";
import DesdobramentoJB from "@/components/metodos/DesdobramentoJB";
import CentenaMatadora from "@/components/metodos/CentenaMatadora";
import PremiumMethodNotice from "@/components/metodos/PremiumMethodNotice";
import DisclaimerEstatistico from "@/components/legal/DisclaimerEstatistico";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAnalytics } from "@/hooks/useAnalytics";

const allMethods = [
  "centena-matadora",
  "desdobramento-jb", "sistema-ia-bicho",
  "cruz-dia", "esquema-infalivel", "dezenas-premiadas", "metodo-777",
  "metodo-sequencia", "centena-bichos", "7-grupos", "centenas-viciadas",
  "bionumerico-stellar", "numerologia-nome", "soma-chave-ouro",
  "calculadora-chave-ouro", "achar-centena-dia"
];

const MetodosPage = () => {
  const { user, getTrialInfo } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const methodFromUrl = searchParams.get("method");
  const { track } = useAnalytics();
  useEffect(() => { track("tool_access", { tool: "metodos" }); }, [track]);
  
  // Garantir que o método inicial seja válido
  const getValidMethod = (method: string | null) => {
    if (method && allMethods.includes(method)) {
      return method;
    }
    return "centena-matadora";
  };
  
  const [activeTab, setActiveTab] = useState(() => getValidMethod(methodFromUrl));
  
  // Atualiza a aba ativa quando o parâmetro da URL mudar
  useEffect(() => {
    if (methodFromUrl && allMethods.includes(methodFromUrl)) {
      setActiveTab(methodFromUrl);
    }
  }, [methodFromUrl]);
  
  // Garantir que a aba ativa seja sempre válida
  useEffect(() => {
    if (!allMethods.includes(activeTab)) {
      setActiveTab("centena-matadora");
    }
  }, [activeTab]);
  
  // Verifica se o usuário tem acesso aos métodos premium
  const hasAccessToPremiumMethods = () => {
    if (!user) return false;
    // Permitir acesso apenas para planos pagos, não para trial/free
    return user.plan && user.plan !== 'free' && user.plan !== 'trial';
  };

  const trialInfo = getTrialInfo();
  // CRÍTICO: Bloquear acesso se trial/assinatura expirou
  const isAccessExpired = trialInfo.status === 'trial_expired' || trialInfo.status === 'subscription_expired';
  const isPremiumUser = hasAccessToPremiumMethods() && !isAccessExpired;

  useEffect(() => {
    if (user && user.plan === 'free' && isAccessExpired) {
      navigate('/planos');
    }
  }, [user, isAccessExpired, navigate]);

  return (
    <>
      <SEOHead
        title="15 Métodos para Jogo do Bicho - Cruz do Dia, Desdobramento, Centenas Viciadas, Método 777"
        description="Métodos exclusivos para o Jogo do Bicho: Cruz do Dia, Esquema Infalível, Dezenas Premiadas, Método 777, Sequência, Centena Bichos, 7 Grupos de Dezenas, Centenas Viciadas, BioNumérico, Numerologia, Soma Bahia, Calculadora Chave de Ouro, Achar Centena do Dia, Jogo do Bicho AI e Desdobramento JB."
        keywords="cruz do dia jogo do bicho, método 777 bicho, dezenas premiadas, centenas viciadas, desdobramento jogo do bicho, soma chave de ouro, calculadora chave de ouro, centena do dia, esquema infalível bicho, numerologia jogo do bicho, bionumérico stellar, 7 grupos dezenas, método sequência bicho, sistema IA bicho, centena bichos, soma bahia, achar centena do dia, desdobramento jb"
        canonical="/metodos"
      />
    <div className="min-h-screen" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="container mx-auto px-4 py-8">
        {/* Título Principal */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-2">
            📘 MÉTODOS IA PALPITE GURU
          </h1>
        </div>
        
        {/* Active Trial Banner */}
        <ActiveTrialBanner />

        {/* Container Principal */}
        <Card className="bg-white shadow-lg rounded-xl border-0 max-w-7xl mx-auto">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              {/* Lista de Abas - Desktop */}
              <div className="hidden lg:block">
                <TabsList className="flex flex-wrap justify-center mb-6 bg-gradient-to-r from-blue-50 to-purple-50 p-2 rounded-xl h-auto gap-2">
                  <TabsTrigger
                    value="centena-matadora"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-600 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap relative"
                  >
                    🎯 Centena Matadora
                    <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[9px] font-bold px-1 rounded">NOVO</span>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="cruz-dia" 
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Cruz do Dia
                  </TabsTrigger>
                  <TabsTrigger 
                    value="esquema-infalivel"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Esquema Infalível
                  </TabsTrigger>
                  <TabsTrigger 
                    value="dezenas-premiadas"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Dezenas Premiadas
                  </TabsTrigger>
                  <TabsTrigger 
                    value="metodo-777"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Método 777
                  </TabsTrigger>
                  <TabsTrigger 
                    value="metodo-sequencia"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Método Sequência
                  </TabsTrigger>
                  <TabsTrigger 
                    value="centena-bichos"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Centena Bichos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="7-grupos"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    7 Grupos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="centenas-viciadas"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Centenas Viciadas
                  </TabsTrigger>
                  <TabsTrigger 
                    value="bionumerico-stellar"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    BioNumérico Stellar
                  </TabsTrigger>
                  <TabsTrigger 
                    value="numerologia-nome"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Numerologia do Nome
                  </TabsTrigger>
                  <TabsTrigger 
                    value="soma-chave-ouro"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Soma Chave de Ouro
                  </TabsTrigger>
                  <TabsTrigger 
                    value="calculadora-chave-ouro"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Calculadora Chave de Ouro
                  </TabsTrigger>
                  <TabsTrigger 
                    value="achar-centena-dia"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Achar Centena do Dia
                  </TabsTrigger>
                  <TabsTrigger 
                    value="sistema-ia-bicho"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-yellow-500 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Sistema IA Bicho
                  </TabsTrigger>
                  <TabsTrigger 
                    value="desdobramento-jb"
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-gray-700 data-[state=active]:to-black data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-105 text-xs font-semibold text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-200 p-3 rounded-lg whitespace-nowrap"
                  >
                    Desdobramento JB
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Dropdown para Mobile */}
              <div className="lg:hidden mb-6">
                <Card className="bg-white border-2 border-blue-300 rounded-xl shadow-lg">
                  <CardContent className="p-2">
                    <select 
                      className="w-full p-4 text-lg font-semibold bg-white border-0 rounded-xl focus:outline-none text-gray-800 cursor-pointer appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em'
                      }}
                      value={activeTab}
                      onChange={(e) => {
                        const targetValue = e.target.value;
                        if (targetValue) {
                          setActiveTab(targetValue);
                        }
                      }}
                    >
                      <option value="centena-matadora">🎯 Centena Matadora (NOVO)</option>
                      <option value="desdobramento-jb">🎰 Desdobramento JB</option>
                      <option value="sistema-ia-bicho">🤖 Sistema IA Bicho</option>
                      <option value="cruz-dia">🎯 Cruz do Dia</option>
                      <option value="esquema-infalivel">✅ Esquema Infalível</option>
                      <option value="dezenas-premiadas">🎲 Dezenas Premiadas</option>
                      <option value="metodo-777">🔢 Método 777</option>
                      <option value="metodo-sequencia">📈 Método Sequência</option>
                      <option value="centena-bichos">🦁 Centena Bichos</option>
                      <option value="7-grupos">📊 7 Grupos</option>
                      <option value="centenas-viciadas">🎯 Centenas Viciadas</option>
                      <option value="bionumerico-stellar">🌟 BioNumérico Stellar</option>
                      <option value="numerologia-nome">🎰 Numerologia do Nome</option>
                      <option value="soma-chave-ouro">🔑 Soma Chave de Ouro</option>
                      <option value="calculadora-chave-ouro">🧮 Calculadora Chave de Ouro</option>
                      <option value="achar-centena-dia">🔍 Achar Centena do Dia</option>
                    </select>
                  </CardContent>
                </Card>
              </div>

              {/* Conteúdo das Abas */}
              <div className="min-h-[600px] overflow-x-auto">
                <TabsContent value="cruz-dia" className="mt-0">
                  <MetodoCruzDia />
                </TabsContent>

                <TabsContent value="esquema-infalivel" className="mt-0">
                  {isPremiumUser ? (
                    <EsquemaInfalivel />
                  ) : (
                    <PremiumMethodNotice methodName="Esquema Infalível" />
                  )}
                </TabsContent>

                <TabsContent value="dezenas-premiadas" className="mt-0">
                  {isPremiumUser ? (
                    <DezenasPremiadas />
                  ) : (
                    <PremiumMethodNotice methodName="Dezenas Premiadas" />
                  )}
                </TabsContent>

                <TabsContent value="metodo-777" className="mt-0">
                  {isPremiumUser ? (
                    <Metodo777 />
                  ) : (
                    <PremiumMethodNotice methodName="Método 777" />
                  )}
                </TabsContent>

                <TabsContent value="metodo-sequencia" className="mt-0">
                  {isPremiumUser ? (
                    <MetodoSequencia />
                  ) : (
                    <PremiumMethodNotice methodName="Método Sequência" />
                  )}
                </TabsContent>

                <TabsContent value="centena-bichos" className="mt-0">
                  {isPremiumUser ? (
                    <CentenaBichos />
                  ) : (
                    <PremiumMethodNotice methodName="Centena Bichos" />
                  )}
                </TabsContent>

                <TabsContent value="7-grupos" className="mt-0">
                  {isPremiumUser ? (
                    <SeteGruposDezenas />
                  ) : (
                    <PremiumMethodNotice methodName="7 Grupos de Dezenas" />
                  )}
                </TabsContent>

                <TabsContent value="centenas-viciadas" className="mt-0">
                  {isPremiumUser ? (
                    <CentenasViciadas />
                  ) : (
                    <PremiumMethodNotice methodName="Centenas Viciadas" />
                  )}
                </TabsContent>

                <TabsContent value="bionumerico-stellar" className="mt-0">
                  {isPremiumUser ? (
                    <BioNumericoStellar />
                  ) : (
                    <PremiumMethodNotice methodName="BioNumérico Stellar" />
                  )}
                </TabsContent>

                <TabsContent value="numerologia-nome" className="mt-0">
                  <NumerologiaNome />
                </TabsContent>

                <TabsContent value="soma-chave-ouro" className="mt-0">
                  {isPremiumUser ? (
                    <SomaBahiaCalculator />
                  ) : (
                    <PremiumMethodNotice methodName="Soma Chave de Ouro" />
                  )}
                </TabsContent>

                <TabsContent value="calculadora-chave-ouro" className="mt-0">
                  {isPremiumUser ? (
                    <CalculadoraChaveOuro />
                  ) : (
                    <PremiumMethodNotice methodName="Calculadora Chave de Ouro" />
                  )}
                </TabsContent>

                <TabsContent value="achar-centena-dia" className="mt-0">
                  {isPremiumUser ? (
                    <AcharCentenaDia />
                  ) : (
                    <PremiumMethodNotice methodName="Achar Centena do Dia" />
                  )}
                </TabsContent>

                <TabsContent value="sistema-ia-bicho" className="mt-0">
                  {isPremiumUser ? (
                    <JogoBichoAI />
                  ) : (
                    <PremiumMethodNotice methodName="Sistema IA Bicho" />
                  )}
                </TabsContent>

                <TabsContent value="desdobramento-jb" className="mt-0">
                  {isPremiumUser ? (
                    <DesdobramentoJB />
                  ) : (
                    <PremiumMethodNotice methodName="Desdobramento JB" />
                  )}
                </TabsContent>

                <TabsContent value="centena-matadora" className="mt-0">
                  {isPremiumUser ? (
                    <CentenaMatadora />
                  ) : (
                    <PremiumMethodNotice methodName="Centena Matadora" />
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Rodapé */}
        <div className="text-center mt-8">
          <p className="text-muted-foreground text-sm">
            Explore os 14 métodos disponíveis. Cada um foi desenvolvido com técnicas testadas para melhorar seus palpites.
          </p>
        </div>

        <DisclaimerEstatistico />
      </div>
    </div>
    </>
  );
};

export default MetodosPage;
